import { wait } from '@idk-web/core-utils';
import { LocalStorage } from '@idk-web/core-ui';
import {
  CodeRequest,
  initPortalLogin,
  getPortalLoginStatus,
  completePortalLogin,
  PortalLoginStatusResponse,
} from '@idk-web/api';

const POLL_INTERVAL_MS = 1000;
const POLL_TIMEOUT_MS = 10 * 60 * 1000;

type InitCallback = (data: PortalLoginStatusResponse) => void;
type CompleteCallback = (
  data: Extract<PortalLoginStatusResponse, { status: 'COMPLETED' }>,
) => void;
type ErrorCallback = (error: unknown) => void;
type ProgressCallback = (
  data: Extract<PortalLoginStatusResponse, { status: 'IN_PROGRESS' }>,
) => void;
type InitFunction = () => Promise<PortalLoginStatusResponse>;
type CancelFunction = (id: string) => Promise<void>;
type GetStatusFunction = (id: string) => Promise<PortalLoginStatusResponse>;

export type PendingPortalLoginRequest = {
  onInit(callback: InitCallback): PendingPortalLoginRequest;
  onComplete(callback: CompleteCallback): PendingPortalLoginRequest;
  onError(callback: ErrorCallback): PendingPortalLoginRequest;
  onProgress(callback: ProgressCallback): PendingPortalLoginRequest;
  start(): StartedPortalLoginRequest;
};

export type StartedPortalLoginRequest = {
  wait(): Promise<void>;
  cancel(): void;
};

export function password(options: {
  username: string;
  password: string;
}): PendingPortalLoginRequest {
  return start(
    () => initPortalLogin({ method: 'PASSWORD', ...options }),
    async () => {
      // Not supported
    },
    async (id) => ({
      id,
      status: 'FAILED',
      error: "Fetched status when init should've been sufficient",
    }),
  );
}

export function bankIdSe(): PendingPortalLoginRequest {
  return start(
    () => initPortalLogin({ method: 'BANKID_SE' }),
    async () => {
      // Not supported
    },
    getPortalLoginStatus,
  );
}

export function freja(): PendingPortalLoginRequest {
  return start(
    () => initPortalLogin({ method: 'FREJA' }),
    async () => {
      // Not supported
    },
    getPortalLoginStatus,
  );
}

export function bankIdNo(window: Window): PendingPortalLoginRequest {
  return startExternal(
    window,
    () =>
      initPortalLogin({ method: 'BANKID_NO' }) as Promise<
        Extract<PortalLoginStatusResponse, { status: 'IN_PROGRESS' }>
      >,
    completePortalLogin,
  );
}

export function mitId(window: Window): PendingPortalLoginRequest {
  return startExternal(
    window,
    () =>
      initPortalLogin({ method: 'MITID' }) as Promise<
        Extract<PortalLoginStatusResponse, { status: 'IN_PROGRESS' }>
      >,
    completePortalLogin,
  );
}

export function ftn(window: Window): PendingPortalLoginRequest {
  return startExternal(
    window,
    () =>
      initPortalLogin({ method: 'FTN' }) as Promise<
        Extract<PortalLoginStatusResponse, { status: 'IN_PROGRESS' }>
      >,
    completePortalLogin,
  );
}

function startExternal(
  window: Window,
  init: () => Promise<
    Extract<PortalLoginStatusResponse, { status: 'IN_PROGRESS' }>
  >,
  login: (
    id: string,
    req: CodeRequest,
  ) => Promise<Extract<PortalLoginStatusResponse, { status: 'COMPLETED' }>>,
): PendingPortalLoginRequest {
  return start(
    async () => {
      const { id, url } = await init();
      window.location.href = url ?? '';
      return { status: 'IN_PROGRESS', id };
    },
    async () => window.close(),
    async (id) => {
      const code = LocalStorage.getItem('code');

      if (code) {
        LocalStorage.removeItem('code');
      } else if (window.closed) {
        return { status: 'FAILED', error: 'cancelled', id };
      } else {
        return { status: 'IN_PROGRESS', id };
      }

      return login(id, { code });
    },
  );
}

function start(
  init: InitFunction,
  cancel: CancelFunction,
  getStatus: GetStatusFunction,
): PendingPortalLoginRequest {
  const initCallbacks: InitCallback[] = [];
  const completeCallbacks: CompleteCallback[] = [];
  const errorCallbacks: ErrorCallback[] = [];
  const progressCallbacks: ProgressCallback[] = [];
  const controller = new AbortController();

  return {
    onInit(callback) {
      initCallbacks.push(callback);
      return this;
    },
    onComplete(callback) {
      completeCallbacks.push(callback);
      return this;
    },
    onError(callback) {
      errorCallbacks.push(callback);
      return this;
    },
    onProgress(callback) {
      progressCallbacks.push(callback);
      return this;
    },
    start() {
      const promise = run(
        init,
        cancel,
        getStatus,
        (data) => initCallbacks.forEach((f) => f(data)),
        (data) => completeCallbacks.forEach((f) => f(data)),
        (error) => errorCallbacks.forEach((f) => f(error)),
        (data) => progressCallbacks.forEach((f) => f(data)),
        controller,
      );

      return {
        wait: () => promise,
        cancel: () => controller.abort(),
      };
    },
  };
}

async function run(
  init: InitFunction,
  cancel: CancelFunction,
  getStatus: GetStatusFunction,
  onInit: InitCallback,
  onComplete: CompleteCallback,
  onError: ErrorCallback,
  onProgress: ProgressCallback,
  controller: AbortController,
): Promise<void> {
  let timeLeft = POLL_TIMEOUT_MS;
  let id: string;

  try {
    const response = await init();
    id = response.id;

    onInit(response);

    switch (response.status) {
      case 'FAILED':
        throw new Error(response.error);
      case 'COMPLETED':
        onComplete(response);
        return;
    }
  } catch (e) {
    onError(e);
    throw e;
  }

  controller.signal.addEventListener('abort', () => cancel(id));

  while (timeLeft > 0) {
    let response: PortalLoginStatusResponse;
    try {
      response = await getStatus(id);
    } catch (e: unknown) {
      onError(e);
      throw e;
    }

    if (controller.signal.aborted || timeLeft < 0) {
      break;
    }

    switch (response.status) {
      case 'FAILED':
        onError(response.error);
        throw new Error(response.error);
      case 'COMPLETED':
        onComplete(response);
        return;
    }

    timeLeft -= POLL_INTERVAL_MS;

    onProgress(response);

    await wait(POLL_INTERVAL_MS);

    if (controller.signal.aborted || timeLeft < 0) {
      break;
    }
  }

  if (controller.signal.aborted) {
    onError('cancelled');
    throw new Error('cancelled');
  } else {
    onError('timed out');
    throw new Error('timed out');
  }
}
